import { useTranslation } from 'react-i18next';
import { X, Upload, FileText, Check } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { apiClient } from '@/api/axios';
import { getAbsoluteUploadUrl } from '@/api/documents';

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentName: string;
  isReplace?: boolean;
  folder?: string;
  onUploadComplete?: (file: {
    name: string;
    size: string;
    type: string;
    url: string;
  }) => void;
}

export function DocumentUploadModal({
  isOpen,
  onClose,
  documentName,
  isReplace = false,
  folder,
  onUploadComplete,
}: DocumentUploadModalProps) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  if (!isOpen) return null;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);

    try {
      const contentType = selectedFile.type || 'application/octet-stream';

      // 1) Ask backend for an upload URL
      const initRes = await apiClient.post<{
        uploadUrl: string;
        key: string;
      }>('/documents/upload-init', {
        filename: selectedFile.name,
        contentType,
        folder: folder || 'documents',
        ttlSeconds: 900,
      });

      const { uploadUrl, key } = initRes.data;
      const absoluteUploadUrl = getAbsoluteUploadUrl(uploadUrl);

      // 2) Upload file to backend (proxied to R2)
      await apiClient.put(absoluteUploadUrl, selectedFile, {
        headers: { 'Content-Type': contentType },
      });

      toast.success(
        `${isReplace ? t('epassportModal.replace') : t('epassportModal.upload')} ${documentName} successfully`
      );

      if (onUploadComplete) {
        onUploadComplete({
          name: selectedFile.name,
          size: `${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB`,
          type: selectedFile.type.split('/')[1]?.toUpperCase() || 'FILE',
          url: key,
        });
      }

      setSelectedFile(null);
      onClose();
    } catch (err: unknown) {
      console.error('Upload failed', err);
      const message =
        err instanceof Error ? err.message : 'Upload failed. Please try again.';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-2xl w-full border dark:border-white/8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b dark:border-white/8">
          <div>
            <h2 className="text-xl font-semibold dark:text-white">
              {isReplace
                ? t('epassportModal.replaceDocument')
                : t('epassportModal.uploadDocument')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-white/70 mt-1">
              {documentName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 dark:text-white" />
          </button>
        </div>

        {/* Upload Area */}
        <div className="p-6">
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-all ${
              isDragging
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-white/20 bg-gray-50 dark:bg-[#0F0E16]'
            }`}
          >
            {!selectedFile ? (
              <>
                <Upload className="w-16 h-16 text-gray-400 dark:text-white/40 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {t('epassportModal.dropFileOrBrowse')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-white/70 mb-4">
                  {t('epassportModal.supportsPdfJpgPng')}
                </p>
                <label className="inline-block">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <span className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg cursor-pointer inline-flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    {t('epassportModal.chooseFile')}
                  </span>
                </label>
              </>
            ) : (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-12 h-12 text-blue-600 dark:text-blue-400" />
                <div className="text-left">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedFile.name}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-white/70">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="ml-4 p-2 hover:bg-gray-200 dark:hover:bg-white/10 rounded-lg"
                >
                  <X className="w-4 h-4 dark:text-white" />
                </button>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-lg p-3">
            <p className="text-xs text-blue-800 dark:text-blue-300">
              {t('epassportModal.noteEncrypted')}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t dark:border-white/8 bg-gray-50 dark:bg-[#0F0E16]">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-white/20 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg text-sm dark:text-white"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 text-sm"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('epassportModal.uploading')}
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {isReplace
                  ? t('epassportModal.replace')
                  : t('epassportModal.upload')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
